import { useState } from 'react';
import type { DateTime } from 'luxon';
import { App, Button, Form, Input, Modal, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ATTACHMENT_MAX_BYTES, type UploadedOrderDto } from '@pams/shared';
import { DatePicker } from '@/components/DatePicker';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { fromPickerDate } from '@/lib/pickerDate';
import { LIBRARY_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §2] THE UPLOAD MODAL — one PDF, a required title, an order date.
 *
 *  ⚠️ IT IS ITS OWN FILE SO `pages/Arrangements/index.tsx` STAYS AT ITS EXEMPTED MODAL COUNT.
 *  `e2e/fixtures/overlays.ts` pins that file at exactly two `<Modal`s and `census.spec.ts`'s
 *  per-file arithmetic is exact — `ConfirmDestructive`'s own header warns about this by name.
 *
 *  ⚠️ `beforeUpload` RETURNS `false`, so antd never uploads anything itself. Its `Upload` posts
 *  to its own `action` URL with its own XHR — no bearer token, no base URL, no envelope handling
 *  — and returning `false` keeps the picker while handing us the `File`.
 *
 *  ⚠️ AND THE THREE PARTS GO IN ONE `FormData`, because the server parses them from ONE stream.
 *  A two-step create-then-upload would invert the bytes-first/row-second ordering and leave a row
 *  pointing at bytes that may never arrive.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const UploadOrderModal = ({ open, onClose }: { open: boolean; onClose: () => void }): React.ReactElement => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ title: string; orderDate: DateTime | null }>();
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: (input: { title: string; orderDate: string; file: File }) => {
      const body = new FormData();
      body.append('file', input.file);
      body.append('title', input.title);
      body.append('orderDate', input.orderDate);
      return api.post<UploadedOrderDto>('/uploaded-orders', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.uploadedOrders.all });
      void message.success('Uploaded');
      close();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /*
   * ⚠️ THE RESET BELONGS TO THE COMMIT HANDLER **AND** TO THE DISMISSAL HANDLER, in the same
   * function — [LIFECYCLE]'s rule. This modal is mounted unconditionally by its host, so state
   * left behind outlives the interaction that produced it and the next open starts half-filled.
   */
  const close = (): void => {
    form.resetFields();
    setFile(null);
    onClose();
  };

  const beforeUpload: UploadProps['beforeUpload'] = (chosen) => {
    if (chosen.size > ATTACHMENT_MAX_BYTES) {
      /* Stated here as well as on the server: the refusal otherwise costs a 5 MB round trip. */
      void message.error(LIBRARY_COPY.tooLarge);
      return false;
    }
    setFile(chosen);
    return false;
  };

  return (
    <Modal
      open={open}
      title={LIBRARY_COPY.modalTitle}
      okText={LIBRARY_COPY.save}
      okButtonProps={{ disabled: file === null, loading: upload.isPending, 'data-testid': 'upload-order-save' }}
      onCancel={close}
      onOk={() => {
        void form.validateFields().then((values) => {
          /* ⚠️ [C1e §4] THE CONVERSION HAPPENS HERE, and the picker's form value is a luxon
             `DateTime` rather than the wire's string — which is why this field carries no
             `zodRule`: that parses the RAW form state and would refuse every date picked. */
          const orderDate = fromPickerDate(values.orderDate);
          if (!file || orderDate === null) return;
          upload.mutate({ title: values.title, orderDate, file });
        });
      }}
      data-testid="upload-order-modal"
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="title"
          label={LIBRARY_COPY.title}
          rules={[{ required: true, message: 'Give this order a title.' }]}
        >
          <Input placeholder={LIBRARY_COPY.titlePlaceholder} maxLength={200} data-testid="upload-order-title" />
        </Form.Item>
        <Form.Item
          name="orderDate"
          label={LIBRARY_COPY.orderDate}
          rules={[{ required: true, message: 'Give this order a date.' }]}
        >
          <DatePicker className="w-full" data-testid="upload-order-date" />
        </Form.Item>
        <Form.Item label={LIBRARY_COPY.file} required>
          <Upload beforeUpload={beforeUpload} showUploadList={false} accept="application/pdf" maxCount={1}>
            <Button data-testid="upload-order-file">{file?.name ?? LIBRARY_COPY.choose}</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
